import * as React from 'react';
import classnames from 'classnames';
import isFunction from 'lodash/isFunction';

import Tooltip from 'components/Tooltip';
import PopoverList from 'components/popover-list/PopoverList';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import InfiniteScrollIndicator from 'components/infinite-load-indicator/InfiniteScrollIndicator';
import Spinner from 'components/spinner/Spinner';

import { __t, applyTranslations } from 'utils/language';
import { getElementInfo } from 'utils/element';

import './sortable-table.scss';

import type { DOMRect, SortableTableColumnType, SortableTableLayout, SortableTableSort } from 'types';

applyTranslations(require('./sortable-table.i18n.json'));

type Props = {
  data: Array<any>,
  dataFooter?: Array<any>,
  columns: Array<SortableTableColumnType>,
  defaultSortingKey: string,
  defaultSortingDirection: 'ascending' | 'descending',
  layout?: SortableTableLayout,
  noScroll?: boolean,
  noSticky?: boolean,
  maxHeight?: number | string,
  extra?: any,
  customClass?: string,
  rowClass?: string,
  tbodyClass?: string,
  getRowStatus?: (row: any) => string | null,
  expandedRows?: Array<number>,
  onSortChanged?: (a: SortableTableSort) => void,
  onRowClicked?: (a: any) => void,
  onChevronClick?: (rowId: number, isRowExpanded: boolean) => void,
  isCollapsible?: (row: any) => boolean,
  renderCollapsedContent?: (row: any, isRowDisabled?: boolean) => React.ReactNode,
  emptyMessage?: string,
  maxVirtualPageSize?: number,
  emptyMessageClassName?: string,
  showSpinner?: boolean
};

type State = {
  ascending: boolean,
  sortingKey: string,
  expandedRows: Array<number>,
  virtualPage: number,
  contentHeight: number | undefined
};

type CssStyles = {
  style: {
    width?: number | string,
    maxHeight?: number | string
  }
}

const getContentHeight = (
  currentElement: DOMRect | null | undefined
): number => (
  currentElement?.height ?? 0
);

export class SortableTable extends React.PureComponent<Props, State> {
  static defaultProps = {
    columns: [],
    data: [],
    defaultSortingKey: '',
    defaultSortingDirection: 'ascending',
    layout: 'enhanced',
    emptyMessageClassName: ''
  };

  state: State = {
    ascending: this.props.defaultSortingDirection === 'ascending',
    sortingKey: this.props.defaultSortingKey,
    expandedRows: [],
    virtualPage: 1,
    contentHeight: undefined
  };

  contentRef: React.RefObject<HTMLTableSectionElement> = React.createRef();

  componentDidMount(): void {
    this.setState({
      contentHeight: getContentHeight(getElementInfo(this.contentRef.current))
    });
  }

  componentDidUpdate(prevProps: Props) {
    const { expandedRows } = this.props;

    this.setState({
      contentHeight: getContentHeight(getElementInfo(this.contentRef.current))
    });

    if (expandedRows && expandedRows !== prevProps.expandedRows) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        expandedRows
      });
    }
  }

  getSortedData = () => {
    const { data, columns } = this.props;
    const { sortingKey, ascending } = this.state;

    if (!sortingKey) {
      return data;
    }

    const activeColumn = columns.filter(col => sortingKey === col.key)[0];
    const sortingFunction = activeColumn && activeColumn.sort;

    if (!sortingFunction) {
      return data;
    }

    // $FlowIgnore
    return data.slice().sort((a, b) => {
      const order = sortingFunction(a, b, ascending);
      return ascending ? order : -order;
    });
  };

  updateSortingProperties = (e: React.SyntheticEvent<HTMLTableCellElement>) => {
    const { columns, onSortChanged, defaultSortingKey } = this.props;
    const { sortingKey, ascending, contentHeight } = this.state;
    const newSortingKey = e.currentTarget.dataset.key;

    const activeColumn = columns.filter(col => newSortingKey === col.key)[0];

    const nextState: State = {
      virtualPage: 1,
      expandedRows: [],
      sortingKey,
      ascending,
      contentHeight
    };

    if (sortingKey === newSortingKey) {
      nextState.sortingKey = sortingKey;
      nextState.ascending = !ascending;
    } else {
      nextState.sortingKey = newSortingKey || defaultSortingKey;
      nextState.ascending = !(activeColumn.initialSortingDirection === 'descending'); // default is ascending
    }

    this.setState(nextState);

    if (onSortChanged) {
      onSortChanged(nextState);
    }
  };

  onRowClick = (rowId: number | undefined | null, isRowExpanded: boolean) => {
    if (!rowId) {
      return;
    }

    const { onChevronClick } = this.props;
    const { expandedRows } = this.state;

    if (isFunction(onChevronClick)) {
      onChevronClick(rowId, !isRowExpanded);
    }

    const newExpandedRows = isRowExpanded ?
      expandedRows.filter(id => id !== rowId) :
      expandedRows.concat(rowId);

    this.setState({
      expandedRows: newExpandedRows
    });
  };

  handleNextVirtualPage = () => {
    this.setState((state) => ({
      virtualPage: state.virtualPage + 1
    }));
  };

  renderSpinner() {
    return (
      <div className="sortable-table__spinner">
        <Spinner
          position="inline"
          size="tiny"
          color="light"
        />
      </div>
    );
  }

  renderTableHead(columns: Array<SortableTableColumnType>) {
    const { layout, noSticky } = this.props;
    const { sortingKey, ascending } = this.state;
    const layoutValue = layout || '';

    return (
      <thead>
        <tr>
          {
            columns.map(column => {
              const headerStyles: CssStyles = {
                style: {}
              };

              if (column.width) {
                headerStyles.style.width = column.width;
              }

              const headerClasses = classnames(
                'sortable-table__header-column',
                column.className,
                {
                  'sortable-table__header-column--sortable': column.sort,
                  'sortable-table__header-column--sorting': column.key === sortingKey,
                  'sortable-table__header-column--sorting-ascending': column.key === sortingKey && ascending,
                  'sortable-table__header-column--sorting-descending': column.key === sortingKey && !ascending,
                  [`sortable-table__cell--align-${String(column.align)}`]: column.align,
                  [`sortable-table__header-column--layout-${layoutValue}`]: layoutValue,
                  'sortable-table__header-column--no-sticky': noSticky
                });

              return (
                <th
                  className={headerClasses}
                  onClick={column.sort && this.updateSortingProperties}
                  key={column.key}
                  data-key={column.key}
                  {...headerStyles}
                >
                  {column.title}
                  {
                    column.titleTooltipMsg && (
                      <span className="sortable-table__header-column-tooltip">
                        <Tooltip
                          width={column.titleTooltipWidth || 'normal'}
                          textAlign={column.titleTooltipTextAlign || 'center'}
                          message={column.titleTooltipMsg}
                          showQuestionMark
                        />
                      </span>
                    )
                  }
                  {
                    column.popoverList && (
                      <span className="sortable-table__header-column-tooltip">
                        <PopoverList
                          list={column.popoverList}
                          width={column.popoverListWidth}
                          lineHeight={column.popoverListLineHeight}
                        />
                      </span>
                    )
                  }
                </th>
              );
            })
          }
        </tr>
      </thead>
    );
  }

  renderTableRow = (row: any, columns: Array<SortableTableColumnType>) => {
    const { layout, extra, rowClass, getRowStatus, renderCollapsedContent, onRowClicked } = this.props;
    const { expandedRows } = this.state;
    const layoutValue = layout || '';
    const rowStatus = getRowStatus ? getRowStatus(row) : null;
    const isRowDisabled = Boolean(row.isRowDisabled);
    const isRowInactive = Boolean(row.isRowInactive);
    const isRowExpanded = expandedRows.includes(row.id);
    const isRowClickable = getRowStatus
      ? Boolean(onRowClicked) && rowStatus
      : Boolean(onRowClicked);

    const rowClasses = classnames('sortable-table__row', rowClass, {
      'sortable-table__row--clickable': isRowClickable,
      'sortable-table__row--selected': Boolean(row.isSelected),
      'sortable-table__row--disabled': isRowDisabled,
      'sortable-table__row--inactive': isRowInactive
    });

    const collapsedContentTriggerClasses = classnames('sortable-table__cell',
      'sortable-table__cell--collapsible', {
        'sortable-table__cell--disabled': isRowDisabled,
        'sortable-table__cell--inactive': isRowInactive
      });

    return (
      <React.Fragment key={row.id}>
        <tr
          key={row.id}
          id={`sortable-table__row-${row.id}`}
          className={rowClasses}
          onClick={onRowClicked ? () => onRowClicked(row) : undefined}
        >
          {
            columns.map((column, index) => {
              const cellClasses = classnames('sortable-table__cell',
                column.className,
                {
                  [`sortable-table__cell--align-${String(column.align)}`]: column.align,
                  [`sortable-table__cell--layout-${layoutValue}`]: layoutValue,
                  [`sortable-table__cell--status-${String(rowStatus)}`]: rowStatus && index === 0,
                  'sortable-table__cell--expanded': expandedRows.includes(row.id),
                  'sortable-table__cell--inactive': isRowInactive
                }
              );

              const contentClasses = classnames('sortable-table__cell-contents',
                column.classNameContents,
                {
                  [`sortable-table__cell-contents--${String(column.display)}`]: column.display
                }
              );

              const styles: CssStyles = {
                style: {}
              };

              if (column.width) {
                styles.style.width = column.width;
              }

              return (
                <td
                  className={cellClasses}
                  key={`${column.key}-${String(row.id)}`}
                  {...styles}
                >
                  <div className={contentClasses}>
                    {column.render(row, extra, isRowExpanded)}
                  </div>
                </td>
              );
            })
          }
        </tr>
        {
          renderCollapsedContent && expandedRows.includes(row.id) && (
            <tr key={`expanded-${String(row.id)}`}>
              <td
                colSpan={columns.length}
                className={collapsedContentTriggerClasses}
              >
                { renderCollapsedContent(row, isRowDisabled) }
              </td>
            </tr>
          )
        }
      </React.Fragment>
    );
  };

  renderTableBody(columns: Array<SortableTableColumnType>, sortedData: Array<any>) {
    const { maxHeight, maxVirtualPageSize, tbodyClass } = this.props;
    const { virtualPage } = this.state;
    const bodyStyles: CssStyles = {
      style: {}
    };

    if (maxHeight) {
      bodyStyles.style.maxHeight = maxHeight;
    }

    let dataToRender = sortedData;
    const renderToItem = virtualPage * (maxVirtualPageSize ?? 0);
    const hasMorePagesToRender = maxVirtualPageSize && renderToItem <= sortedData.length;

    if (hasMorePagesToRender) {
      dataToRender = dataToRender.slice(0, renderToItem);
    }

    const tbodyClasses = classnames('sortable-table__tbody', tbodyClass);

    return (
      <tbody
        ref={this.contentRef}
        className={tbodyClasses}
        {...bodyStyles}
      >
        {
          dataToRender.map(row => this.renderTableRow(row, columns))
        }
        {hasMorePagesToRender && (
          <tr>
            <td className="sortable-table__pagination">
              <InfiniteScrollIndicator
                isLoading={false}
                onLoadMore={this.handleNextVirtualPage}
              />
            </td>
          </tr>
        )}
      </tbody>
    );
  }

  renderTableFooter(columns: Array<SortableTableColumnType>, sortedData: Array<any>) {
    const { layout, dataFooter, emptyMessage, emptyMessageClassName } = this.props;
    const layoutValue = layout || '';

    return (
      <tfoot>
        {
          dataFooter && (
            <tr>
              {
                columns.map((column, index) => {
                  const columnObj = dataFooter[index];
                  const cellClasses = classnames({
                    'sortable-table__cell': true,
                    'sortable-table__cell-footer': true,
                    [`sortable-table__cell--align-${String(column.align)}`]: column.align,
                    [`sortable-table__cell--layout-${layoutValue}`]: layoutValue
                  });

                  const contentClasses = classnames({
                    'sortable-table__cell-contents': true,
                    [`sortable-table__cell-contents--${String(column.display)}`]: column.display
                  });

                  const styles: CssStyles = {
                    style: {}
                  };

                  if (column.width) {
                    styles.style.width = column.width;
                  }

                  return (
                    <td
                      className={cellClasses}
                      key={`${column.key}-${String(columnObj.total)}`}
                      {...styles}
                    >
                      <div className={contentClasses}>
                        {
                          column.renderFooter &&
                          column.renderFooter(columnObj)
                        }
                      </div>
                    </td>
                  );
                })
              }
            </tr>
          )
        }
        {
          sortedData.length === 0 && (
            <tr>
              <td className="sortable-table__cell" colSpan={columns.length}>
                <span className={emptyMessageClassName}>
                  {emptyMessage || __t('sortable_table.no_data')}
                </span>
              </td>
            </tr>
          )
        }
      </tfoot>
    );
  }

  render() {
    const {
      columns: initialColumns,
      layout,
      noScroll,
      noSticky,
      maxHeight,
      customClass,
      showSpinner,
      isCollapsible,
      renderCollapsedContent
    } = this.props;
    const { expandedRows } = this.state;
    const layoutValue = layout || '';
    const bodyStyles: CssStyles = {
      style: {}
    };

    const sortedData = this.getSortedData();

    const tableClasses = classnames({
      'sortable-table': true,
      [`sortable-table--layout-${layoutValue}`]: layoutValue,
      'sortable-table--no-scroll': noScroll,
      'sortable-table--no-sticky': noSticky,
      [String(customClass)]: customClass
    });

    if (maxHeight) {
      bodyStyles.style.maxHeight = maxHeight;
    }

    let columns = initialColumns;

    if (renderCollapsedContent) {
      const additionalColumn: SortableTableColumnType = {
        title: '',
        align: 'left',
        key: 'sortable-table-collapse-actions',
        className: 'sortable-table__expand-button-wrapper',
        render: row => {
          if (!isCollapsible || !isCollapsible(row)) {
            return null;
          }

          const isRowExpanded = expandedRows.includes(row.id);
          const expandButtonClassnames = classnames('sortable-table__expand-button', {
            'sortable-table__expand-button--expanded': isRowExpanded
          });

          return (
            <div
              key={`sortable-table__expand-button--row-${row.id}`}
              className="sortable-table__expand-button-container"
              onClick={() => this.onRowClick(row.id, isRowExpanded)}
            >
              <div className={expandButtonClassnames}>
                <SVGDynamicIcon
                  clickable
                  name="chevron-down-custom-fg"
                  customColor="grey"
                  width={16}
                  height={16}
                />
              </div>
            </div>
          );
        }
      };

      columns = columns.concat([additionalColumn]);
    }

    return (
      <div className={tableClasses}>
        <table>
          {this.renderTableHead(columns)}
          {this.renderTableBody(columns, sortedData)}
          {this.renderTableFooter(columns, sortedData)}
        </table>
        {showSpinner && this.renderSpinner()}
      </div>
    );
  }
}

export default SortableTable;
